import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ROUTES } from '@/constants/routes'
import { useAuthContext } from '@/context'
import { useLoading } from '@/context/loading-context'
import { queryClient } from '@/queryClient'
import { useSwitchBusiness } from '@/services/auth/mutations'
import { keyUser } from '@/services/auth/queries'
import { Building2, Check, Plus } from 'lucide-react'
import { useNavigate } from 'react-router'

export function BusinessesDropdown({ trigger }: { trigger: React.ReactNode }) {
    const navigate = useNavigate()
    const { businessAccounts, setActiveBusiness, activeBusiness, user } = useAuthContext()
    const { showLoading, hideLoading } = useLoading()
    const isMobileConsumer = user?.experience_variant === 'mobile_consumer'

    const { mutate: switchBusiness } = useSwitchBusiness({
        refetch: [keyUser],
        onSuccess: () => {
            navigate(ROUTES.DASHBOARD_ROOT)
            queryClient.invalidateQueries()
            hideLoading()
        },
    })

    const handleSwitchBusiness = (businessId: string) => {
        showLoading('Switching business...')
        setActiveBusiness(businessId)
        switchBusiness({ business: businessId })
    }


    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
            <DropdownMenuContent className='w-56 bg-white border border-gray-200 shadow-2xl' align='start' forceMount>
                <DropdownMenuLabel className='font-normal'>
                    <p className='text-sm font-medium leading-none'>Switch Business</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className='bg-gray-300' />

                {businessAccounts &&
                    businessAccounts?.length > 0 &&
                    businessAccounts
                        ?.sort((a, b) => (a.business.name || '').localeCompare(b.business.name || ''))
                        .map((business, index) => {
                        const biz = business.business
                        const businessId = biz?.id || (biz as any)?._id?.toString()
                        const isActive = businessId === activeBusiness
                        return (
                            <DropdownMenuGroup key={index}>
                                <DropdownMenuItem
                                    className={`py-2 ${isActive ? 'bg-muted cursor-default' : ''}`}
                                    onClick={() => !isActive && businessId && handleSwitchBusiness(businessId)}
                                    disabled={isActive}
                                >
                                    <Building2 className='w-4 h-4 mr-2' />
                                    <span>{business.business.name}</span>
                                    {isActive && <Check className='w-4 h-4 ml-auto' />}
                                </DropdownMenuItem>
                            </DropdownMenuGroup>
                        )
                    })}

                <DropdownMenuSeparator className='bg-gray-300' />

                {isMobileConsumer ? (
                    <div className='px-3 py-2 text-xs text-gray-600'>
                        Mobile accounts have one business. Agency tools (multiple businesses) are available in full SaaS.
                    </div>
                ) : (
                    <DropdownMenuItem
                        className='flex items-center justify-center w-full gap-3 py-2 border shadow-sm bg-primary-light hover:!bg-primary-light text-sm'
                        onClick={() => navigate('/new-business')}
                    >
                        <Plus size={18} />
                        <span>New business</span>
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
